/**
 * Gets the App config.
 *
 * @param ctx
 *  The context object from Next.js that holds information about the request.
 */

import gql from 'graphql-tag';
import initApollo from '../apollo/initApollo';
import { AppConfig } from '../models/config';

let enabledModules: string[] = [];

/**
 * Gets the App configuration like feature flags or other info from the backend.
 *
 * @param ctx
 * The context object from Next.js that holds information about the request.
 * @returns
 * The App config.
 */
export const getAppConfig = async (ctx): Promise<AppConfig[]> => {
  const apolloClient = initApollo({}, ctx);
  const { data } = await apolloClient.query({
    query: gql`
      query getAppConfig {
        getAppConfig {
          name
          key
          value
        }
      }
    `,
  });

  const config = data.getAppConfig ?? [];
  setEnabledModules(config);
  return config;
};

/**
 * Stores the enabled modules used by isModuleEnabled().
 *
 * getAppConfig() only runs on the server for the initial page load, so the
 * client has to call this with the config. Otherwise queries built in
 * the browser would not see the enabled modules.
 *
 * @param config
 * The App config.
 */
export const setEnabledModules = (config: AppConfig[]): void => {
  enabledModules = getEnabledModules(config ?? []);
};

export const isModuleEnabled = (moduleName: string): boolean => {
  return enabledModules.includes(moduleName);
};

export const getServerConfigByName = (config: AppConfig[], name: string) => {
  return config.find((c) => c.name === name);
};

/**
 * Gets the names of the backend modules the App config reports as enabled.
 *
 * Only the modules listed under frontend_app_config.module_state in the backend
 * are reported; each carries "1" when enabled and "" when not.
 *
 * @param config
 * The App config.
 * @returns
 * The enabled module names.
 */
export const getEnabledModules = (config: AppConfig[]): string[] => {
  return config
    .filter((c) => c.key === 'module_state' && !!c.value[0])
    .map((c) => c.name);
};
